import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, RotateCcw } from "lucide-react";
import { SITUATIONS } from "@/data/situations";
import { useApp } from "@/context/AppContext";

const STAGES = [
  { key: "act", label: "0–10 SEC", title: "Immediate action", start: 0, end: 10 },
  { key: "contact", label: "10–30 SEC", title: "Who to contact", start: 10, end: 30 },
  { key: "plan", label: "30–60 SEC", title: "What to do next", start: 30, end: 60 },
];

const CONTENT = {
  emergency: {
    act: "Move away from danger. Do not stay in the line of fire.",
    contact: "Call emergency services. Speak your location first.",
    plan: "Follow dispatcher instructions. Message your Trust Circle.",
  },
  lost: {
    act: "Stop. Look for the nearest visible, populated place.",
    contact: "Share your live location with someone you trust.",
    plan: "Walk toward a public place. Confirm your bus/route.",
  },
  find: {
    act: "Stay near where you last saw them. Call their phone.",
    contact: "Alert on-site security or a nearby staff member.",
    plan: "Head to the information desk. Notify family.",
  },
  help: {
    act: "Check if the scene is safe for you first.",
    contact: "Call the correct emergency service.",
    plan: "Stay calm, follow instructions, keep bystanders back.",
  },
  "someone-needs": {
    act: "Do not move them. Check breathing.",
    contact: "Call emergency services with clear location.",
    plan: "Stay with the person. Note what happened.",
  },
  scam: {
    act: "Stop. Do not click, reply or share codes.",
    contact: "Call the number on your bank card, not the message.",
    plan: "Change passwords. Report to fraud line.",
  },
  unsafe: {
    act: "Move toward a lit public place. Do not confront.",
    contact: "Stay on a live call with someone you trust.",
    plan: "Enter a shop or transport. Note descriptions.",
  },
};

export function OneMinute() {
  const [pickId, setPickId] = useState("lost");
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const raf = useRef(null);
  const startedAt = useRef(0);
  const { speak } = useApp();
  const lastSpoken = useRef("");

  useEffect(() => {
    if (!running) return;
    startedAt.current = performance.now() - seconds * 1000;
    const tick = () => {
      const t = (performance.now() - startedAt.current) / 1000;
      if (t >= 60) {
        setSeconds(60);
        setRunning(false);
        return;
      }
      setSeconds(t);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const stageIdx = seconds < 10 ? 0 : seconds < 30 ? 1 : 2;
  const stage = STAGES[stageIdx];
  const content = CONTENT[pickId] || CONTENT.lost;
  const stageText = content[stage.key];

  useEffect(() => {
    if (!running) return;
    const key = `${pickId}-${stage.key}`;
    if (lastSpoken.current !== key) {
      lastSpoken.current = key;
      speak(`${stage.title}. ${stageText}`);
    }
  }, [stage.key, pickId, running, stageText, speak, stage.title]);

  const pct = seconds / 60;
  const circumference = 2 * Math.PI * 88;
  const dash = circumference * pct;

  const reset = () => {
    setSeconds(0);
    setRunning(false);
    lastSpoken.current = "";
  };

  return (
    <section id="one-minute" className="relative py-20 md:py-28">
      <div className="absolute inset-0 tac-grid-fine opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)]">STEP 03 // ONE MINUTE</span>
          <span className="h-px w-14 bg-[color:var(--lg-line-strong)]" />
        </div>
        <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight max-w-3xl">
          Give LIFEGRID <span className="text-lime-accent">60&nbsp;seconds</span>. We&apos;ll turn confusion into a plan.
        </h2>

        <div className="mt-12 grid md:grid-cols-2 gap-10 items-center">
          <div className="flex flex-col items-center">
            <div className="relative h-[220px] w-[220px] md:h-[260px] md:w-[260px]">
              <svg viewBox="0 0 200 200" className="absolute inset-0 -rotate-90">
                <circle cx="100" cy="100" r="88" stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
                <circle
                  cx="100"
                  cy="100"
                  r="88"
                  stroke="var(--lg-lime)"
                  strokeWidth="6"
                  strokeLinecap="round"
                  fill="none"
                  strokeDasharray={`${dash} ${circumference}`}
                  style={{ filter: "drop-shadow(0 0 12px rgba(204,255,0,0.4))" }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)]">{stage.label}</div>
                <div className="font-display text-5xl md:text-6xl font-extrabold mt-1" data-testid="one-minute-seconds">
                  {String(Math.floor(seconds)).padStart(2, "0")}
                </div>
                <div className="text-xs text-[color:var(--lg-text-3)] font-mono-tac tracking-widest mt-1">/ 60</div>
              </div>
            </div>
            <div className="mt-6 flex gap-2">
              <button
                className="btn-tactile primary"
                onClick={() => setRunning((r) => !r)}
                data-testid="one-minute-play"
              >
                {running ? <Pause size={14} /> : <Play size={14} />}
                {running ? "Pause" : seconds > 0 ? "Resume" : "Start"}
              </button>
              <button className="btn-tactile" onClick={reset} data-testid="one-minute-reset">
                <RotateCcw size={14} /> Reset
              </button>
            </div>
          </div>

          <div>
            <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-text-3)] mb-3">SITUATION</div>
            <div className="flex flex-wrap gap-2">
              {SITUATIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setPickId(s.id);
                    reset();
                  }}
                  data-testid={`one-minute-pick-${s.id}`}
                  className={`btn-tactile ${pickId === s.id ? "primary" : ""}`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <div className="mt-8 space-y-3">
              {STAGES.map((st, i) => {
                const active = i === stageIdx && running;
                return (
                  <motion.div
                    key={st.key}
                    layout
                    animate={{ opacity: i === stageIdx ? 1 : 0.55 }}
                    className={`glass rounded-md p-4 border ${
                      active ? "border-[color:var(--lg-lime)]/60" : "border-white/10"
                    }`}
                    data-testid={`one-minute-stage-${st.key}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)]">
                        {st.label}
                      </div>
                      <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-text-3)]">
                        {String(i + 1).padStart(2, "0")} / 03
                      </div>
                    </div>
                    <div className="mt-2 font-display text-lg md:text-xl font-bold uppercase">{st.title}</div>
                    <AnimatePresence mode="wait">
                      <motion.p
                        key={`${pickId}-${st.key}`}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="text-sm text-[color:var(--lg-text-2)] mt-1"
                      >
                        {content[st.key]}
                      </motion.p>
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
